import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { Card, Spin, Alert, Typography, Space } from 'antd';
import { fetchTickerData } from "../../../api/tickersApi";
import { fetchDetailedPortfolioObjects } from "../../../api/portfolioApi";
import usePortfolioTimeseries from "../../PortfolioView/PortfolioChart/usePortfolioTimeseries";
import MultiStockChart from "../../../SuperComponents/StockCharts/MultiStockChart";
import BenchmarkMetrics from "./BenchmarkMetrics";
import { portfolioTypeString } from "../../../utils/strings";
import { Brand } from "../../../components/Graphics/Brand";
import { useTickerSearchIndex } from "../../../modules/Search/useTickerSearchIndex";
import TickerChip from "../../../components/TickerChip";
import { getTickersBySymbols } from "../../../store/slices/globalResources.slice";
import {TrackChangesRounded} from "@mui/icons-material";
import usePdfExport from "../../../hooks/usePdfExport";
import Toolbar from "./Toolbar";

const SectionHeader = ({ text }) => (
  <div className="w-full border-b border-gray-200 py-4">
    <Typography.Title level={4} className="text-gray-800 font-medium">
      {text}
    </Typography.Title>
  </div>
);

const FactSheet = () => {
  const { portfolioId } = useParams();
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);

  const portfolio = useSelector(state => state.portfolios.items.find(p => p.id === portfolioId));
  const portfolioStats = useSelector(state => state.tickers.tickers[portfolioId]);
  const symbols = useMemo(() => [portfolioId, 'spy'], [portfolioId]);
  const startDate = useMemo(() => '2020-01-01', []);
  const tickerPool = Object.keys(portfolio?.symbols ?? {});

  const { tickers } = useTickerSearchIndex(tickerPool || ['spy']);
  const tickerDetails = useSelector(state => getTickersBySymbols(state, tickerPool));
  const { timeSeriesData, loading, error } = usePortfolioTimeseries(symbols, startDate);

  const { exportToPDF, componentRef, isExporting } = usePdfExport({
    filename: 'benchmark_metrics.pdf',
    onBeforeExport: () => setEditMode(false),
    onAfterExport: () => setEditMode(true),
  });

  useEffect(() => {
    if (portfolioId) {
      setLoadingDetails(true);
      Promise.all([
        dispatch(fetchDetailedPortfolioObjects([portfolioId])),
        dispatch(fetchTickerData({
          symbols: [portfolioId],
          fields: ['stats.benchmarks.spy'],
          collection: 'stats'
        }))
      ])
        .then(() => setLoadingDetails(false))
        .catch(err => {
          console.error(err);
          setErrorDetails('Failed to fetch portfolio data');
          setLoadingDetails(false);
        });
    }
  }, [dispatch, portfolioId]);

  if (loadingDetails || loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin className="text-primary" />
      </div>
    );
  }

  if (errorDetails || error) {
    return (
      <Alert variant="destructive">
        <Typography.Text>{errorDetails || error}</Typography.Text>
      </Alert>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto bg-transparent p-6">
        <div className="mb-6">
          <Toolbar
            onExportPDF={exportToPDF}
            editMode={editMode}
            toggleEditMode={() => setEditMode(!editMode)}
          />
        </div>

        <div ref={componentRef} className="w-[8.5in] mx-auto">
          <Card>
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <Space className="items-center">
                  <Brand maxHeight={50} />
                  <div>
                    <Typography.Title level={3} className="mb-0">
                      {portfolio?.name}
                    </Typography.Title>
                    <Typography.Text className="text-gray-600">
                      {portfolioTypeString(portfolio?.type)}
                    </Typography.Text>
                  </div>
                </Space>

                <div className="flex items-center gap-2">
                  <TrackChangesRounded className="h-6 w-6 text-primary" />
                  <Typography.Title level={4} className="mb-0">
                    {portfolio?.targets?.equity} / {portfolio?.targets?.fixed}
                  </Typography.Title>
                </div>
              </div>
            </div>

            {portfolio?.attr?.summary && (
              <div className="p-6 border-b border-gray-200">
                <Typography.Text className="font-medium">
                  {portfolio.attr.summary}
                </Typography.Text>
              </div>
            )}

            <div className="p-6 border-b border-gray-200">
              <MultiStockChart
                symbols={[portfolioId, 'spy']}
                labelMapping={{[portfolioId]: portfolio?.name, spy: "S&P 500"}}
                stockChartProps={{
                  showRangeSelector: false
                }}
                height={300}
                heightOffset={0}
                xPadding={0}
                yPadding={0}
              />
              <Typography.Text className="text-xs text-gray-500 mt-2">
                Illustrative Performance of the Portfolio vs Benchmark.
              </Typography.Text>
            </div>

            {portfolioStats && (
              <div className="p-6 border-b border-gray-200">
                <SectionHeader text="Portfolio vs Benchmark Metrics" />
                <div className="mt-4">
                  <BenchmarkMetrics
                    benchmarkMetrics={portfolioStats?.stats?.benchmarks?.spy}
                    editMode={editMode}
                  />
                </div>
              </div>
            )}

            <div className="p-6">
              <SectionHeader text="Portfolio Options" />
              <div className="mt-4 grid grid-cols-2 gap-4">
                {Array.isArray(tickerPool) && tickerPool.map(symbol => (
                  <TickerChip
                    key={symbol}
                    ticker={tickerDetails.find(x => x.symbol === symbol)}
                  />
                ))}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FactSheet;